// 桌面文件夹图标(spec 260508-folder-feature §5.3)。
//
// 96×96 .os-icon 外壳 + .os-folder-preview 九宫格内容(取成员前 9 个 App 的
// icon 缩略;1 个 / 2-4 个 / 5-9 个三档 layout)。
// 单击 / Enter / Space → onActivate(folderId)。双击不绑定 —— 双击进入重命名
// 是弹层标题区的事(避免桌面图标双击与单击冲突)。
//
// 拖拽相关 props 由 M5 desktopDnD.ts 注入。

import { useState, type CSSProperties, type DragEvent } from 'react';

import type { DesktopFolderDTO } from '@blade/os-sdk';

import type { Registry, RegistryEntry } from '../apps/registryContext';
import { AppIcon } from './AppIcon';
import { DesktopIconLabel } from './DesktopIconLabel';
import {
  clearActiveDragPayload,
  deserializePayload,
  getActiveDragPayload,
  serializePayload,
  type DragPayload,
} from './desktopDnD';

interface FolderIconProps {
  folder: DesktopFolderDTO;
  registry: Registry;
  /** 没有显式坐标时按数组顺序作为 CSS `order` 渲染。 */
  fallbackIndex: number;
  onActivate: (folderId: string) => void;
  /** dispatcher 注入;源拖拽结束时 emit 落点 + payload。 */
  onDragEnd?: (event: DragEvent<HTMLButtonElement>, payload: DragPayload) => void;
}

function previewClass(count: number): string {
  // count 已经被钳到 [1,9],这里 cap 防御。
  const n = Math.max(1, Math.min(9, count));
  return `os-folder-preview os-folder-preview--count-${n}`;
}

export function FolderIcon({
  folder,
  registry,
  fallbackIndex,
  onActivate,
  onDragEnd,
}: FolderIconProps) {
  // 先过滤掉 registry 里查不到的成员再取前 9 个。
  //
  // 之前是直接 slice(0,9) 再逐个 registry.get,取不到就渲染一个空 <span> 占位 ——
  // 九宫格里会出现空洞,而且这些洞还占掉名额,把后面本来能显示的图标挤出预览。
  // 成员查不到是常态:app 被卸载 / 从 apps[] 注销(如 network-config)后,老用户
  // 布局里那条引用仍在。弹层(FolderPopover)本来就是先 filter 再渲染,这里对齐它。
  const previewMembers = folder.items
    .map((appId) => ({ appId, entry: registry.get(appId) }))
    .filter((m): m is { appId: string; entry: RegistryEntry } => Boolean(m.entry))
    .slice(0, 9);
  const placement: CSSProperties = { order: fallbackIndex };
  const cellSize = previewMembers.length === 1 ? 32 : previewMembers.length <= 4 ? 18 : 12;

  // 拖入接收态:dragenter 加 highlight,dragleave / drop 后清除。
  const [receiving, setReceiving] = useState(false);

  const dragPayload: DragPayload = {
    source: 'desktop',
    itemType: 'folder',
    folderId: folder.id,
  };

  return (
    <button
      type="button"
      role="gridcell"
      aria-label={`文件夹: ${folder.name}`}
      data-desktop-kind="folder"
      data-folder-id={folder.id}
      style={placement}
      onClick={() => onActivate(folder.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(folder.id);
        }
      }}
      // 自身可拖(进行桌面排序),也接受其他 App 拖入。
      draggable
      onDragStart={(e) => serializePayload(e.dataTransfer, dragPayload)}
      onDragEnd={(e) => {
        onDragEnd?.(e, dragPayload);
        clearActiveDragPayload();
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        const payload =
          deserializePayload(e.dataTransfer) ?? getActiveDragPayload();
        setReceiving(
          payload?.source === 'desktop' && payload.itemType === 'app',
        );
      }}
      onDragOver={(e) => {
        // 必须 preventDefault 才能让浏览器接受 drop;不阻止 dispatcher 后续判断。
        e.preventDefault();
      }}
      onDragLeave={() => setReceiving(false)}
      onDrop={() => setReceiving(false)}
      title={folder.name}
      className={`os-icon relative${receiving ? ' os-folder-receiving' : ''}`}
    >
      <div className="os-icon-tile">
        <div className={previewClass(previewMembers.length)}>
          {previewMembers.map(({ appId, entry }) => (
            <AppIcon
              key={appId}
              icon={entry.icon}
              title={entry.title}
              size={cellSize}
            />
          ))}
        </div>
      </div>
      <DesktopIconLabel text={folder.name} />
    </button>
  );
}
